import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpRight, Mail, MapPin, Loader2 } from 'lucide-react';
import { Reveal, SectionLabel } from './Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, type = 'text', value, onChange, testid, required, textarea }) => (
  <label className="block">
    <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
      {label}
      {required && <span className="text-gold"> *</span>}
    </span>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={5}
        data-testid={testid}
        className="w-full resize-none border border-white/15 bg-white/5 px-4 py-3 text-white placeholder-slate-500 outline-none transition-colors focus:border-gold focus:ring-1 focus:ring-gold"
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full border border-white/15 bg-white/5 px-4 py-3 text-white placeholder-slate-500 outline-none transition-colors focus:border-gold focus:ring-1 focus:ring-gold"
      />
    )}
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', organisation: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Enquiry received', {
        description: 'Thank you — our team will be in touch shortly.',
      });
      setForm({ name: '', email: '', organisation: '', subject: '', message: '' });
    } catch (err) {
      toast.error('Something went wrong', {
        description: 'Please try again or email us directly.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-t border-white/5 bg-navy-900 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel>Contact</SectionLabel>
              <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                Begin a conversation.
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-slate-300">
                Whether you represent a government, an institution or an
                investment partner, we welcome the opportunity to discuss your
                mandate in confidence.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-12 space-y-6">
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 items-center justify-center border border-white/10 text-gold">
                    <Mail className="h-4 w-4" strokeWidth={1.5} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                      Enquiries
                    </div>
                    <a
                      href="mailto:info@londonbiotechint.com"
                      data-testid="contact-email-link"
                      className="mt-1 block text-white transition-colors hover:text-gold"
                    >
                      info@londonbiotechint.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 items-center justify-center border border-white/10 text-gold">
                    <MapPin className="h-4 w-4" strokeWidth={1.5} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                      Registered office
                    </div>
                    <div className="mt-1 text-white">London, United Kingdom</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="border border-white/10 bg-navy-950 p-8 lg:p-10"
              >
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field label="Full name" name="name" value={form.name} onChange={onChange} required testid="contact-name" />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="contact-email" />
                </div>
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field label="Organisation" name="organisation" value={form.organisation} onChange={onChange} testid="contact-organisation" />
                  <Field label="Subject" name="subject" value={form.subject} onChange={onChange} testid="contact-subject" />
                </div>
                <div className="mt-6">
                  <Field label="Message" name="message" value={form.message} onChange={onChange} required textarea testid="contact-message" />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="group mt-8 inline-flex items-center gap-3 border border-gold bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy-950 transition-all duration-300 hover:bg-gold-bright hover:border-gold-bright disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      Send Enquiry
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
